import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { KPICard } from '../../components/ui/KPICard';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { DataTable } from '../../components/ui/DataTable';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { ShoppingCartIcon, ReceiptIcon, CreditCardIcon, FileTextIcon, ArrowRightIcon, AlertCircleIcon, PlusIcon, MessageSquareIcon, TruckIcon } from 'lucide-react';
// Mock data for the dashboard
const recentPOs = [{
  id: 'PO-2026-8892',
  date: 'Mar 15, 2026',
  amount: '$45,200.00',
  status: 'Pending'
}, {
  id: 'PO-2026-8891',
  date: 'Mar 14, 2026',
  amount: '$12,450.00',
  status: 'Active'
}, {
  id: 'PO-2026-8885',
  date: 'Mar 10, 2026',
  amount: '$8,900.00',
  status: 'Delivered'
}];
const urgentTasks = [{
  id: 1,
  title: 'Acknowledge PO-2026-8892',
  due: 'Today',
  type: 'Order'
}, {
  id: 2,
  title: 'Upload renewed Halal Certification',
  due: 'Tomorrow',
  type: 'Compliance'
}, {
  id: 3,
  title: 'Review proposed contract amendment',
  due: 'Mar 18',
  type: 'Contract'
}];
export const SupplierDashboard: React.FC = () => {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-1809">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2" data-id="element-1810">
        <div data-id="element-1811">
          <h1 className="text-2xl font-bold text-text-primary" data-id="element-1812">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-text-secondary" data-id="element-1813">
            {user?.company} • Supplier ID: 1004582
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1814">
          <Button variant="secondary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1816" />} data-id="element-1815">
            Create Invoice
          </Button>
          <Button variant="primary" onClick={() => navigate('/supplier/tasks')} data-id="element-1817">
            View Tasks (9)
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1818">
        <KPICard title="Open Purchase Orders" value="14" icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-1820" />} onClick={() => navigate('/supplier/orders')} data-id="element-1819" />
        <KPICard title="Pending Invoices" value="3" icon={<ReceiptIcon className="w-5 h-5" data-id="element-1822" />} trend="up" trendValue="Action required" onClick={() => navigate('/supplier/invoices')} data-id="element-1821" />
        <KPICard title="Upcoming Payments" value="$298.5K" icon={<CreditCardIcon className="w-5 h-5" data-id="element-1824" />} onClick={() => navigate('/supplier/payments')} data-id="element-1823" />
        <KPICard title="Expiring Documents" value="1" icon={<FileTextIcon className="w-5 h-5" data-id="element-1826" />} trend="down" trendValue="Due in 5 days" onClick={() => navigate('/supplier/documents')} data-id="element-1825" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1827">
        {/* Left Column (2/3) */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1828">
          {/* Recent POs */}
          <Card data-id="element-1829">
            <div className="flex justify-between items-center mb-4" data-id="element-1830">
              <h2 className="text-lg font-semibold text-text-primary" data-id="element-1831">
                Latest Purchase Orders
              </h2>
              <Button variant="ghost" size="sm" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-1833" />} onClick={() => navigate('/supplier/orders')} data-id="element-1832">
                View All
              </Button>
            </div>
            <DataTable data={recentPOs} columns={[{
            header: 'PO Number',
            accessorKey: 'id'
          }, {
            header: 'Date',
            accessorKey: 'date'
          }, {
            header: 'Amount',
            accessorKey: 'amount',
            align: 'right'
          }, {
            header: 'Status',
            cell: row => <StatusPill status={row.status} data-id="element-1835" />
          }]} onRowClick={() => navigate('/supplier/orders')} data-id="element-1834" />
          </Card>

          {/* Active Sourcing Events */}
          <Card data-id="element-1836">
            <div className="flex justify-between items-center mb-4" data-id="element-1837">
              <h2 className="text-lg font-semibold text-text-primary" data-id="element-1838">
                Active Sourcing Events
              </h2>
            </div>
            <div className="border border-border-light rounded-fluent p-4 flex justify-between items-center hover:bg-surface-secondary cursor-pointer transition-colors" onClick={() => navigate('/supplier/sourcing')} data-id="element-1839">
              <div data-id="element-1840">
                <div className="flex items-center gap-2 mb-1" data-id="element-1841">
                  <Badge variant="info" size="sm" data-id="element-1842">
                    RFP
                  </Badge>
                  <span className="font-medium text-text-primary" data-id="element-1843">
                    Fresh Produce Supply 2026-2027
                  </span>
                </div>
                <p className="text-sm text-text-secondary" data-id="element-1844">
                  Submission deadline: Mar 25, 2026
                </p>
              </div>
              <Button variant="secondary" size="sm" data-id="element-1845">
                Continue Draft
              </Button>
            </div>
          </Card>
        </div>

        {/* Right Column (1/3) */}
        <div className="space-y-6" data-id="element-1846">
          {/* Tasks Due Now */}
          <Card className="border-l-4 border-l-status-warning" data-id="element-1847">
            <div className="flex justify-between items-center mb-4" data-id="element-1848">
              <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2" data-id="element-1849">
                <AlertCircleIcon className="w-5 h-5 text-status-warning" data-id="element-1850" />
                Tasks Due Now
              </h2>
            </div>
            <div className="space-y-3" data-id="element-1851">
              {urgentTasks.map(task => <div key={task.id} className="p-3 bg-surface-secondary rounded-fluent cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => navigate('/supplier/tasks')} data-id="element-1852">
                  <div className="flex justify-between items-start mb-1" data-id="element-1853">
                    <span className="text-sm font-medium text-text-primary" data-id="element-1854">
                      {task.title}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-text-secondary" data-id="element-1855">
                    <span data-id="element-1856">{task.type}</span>
                    <span className={task.due === 'Today' ? 'text-status-error font-medium' : ''} data-id="element-1857">
                      {task.due}
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Account Health */}
          <Card data-id="element-1858">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1859">
              Profile Completion
            </h2>
            <ProgressBar progress={85} label="Overall Status" variant="success" className="mb-4" data-id="element-1860" />
            <ul className="space-y-2 text-sm text-text-secondary" data-id="element-1861">
              <li className="flex justify-between" data-id="element-1862">
                <span data-id="element-1863">Company Details</span>{' '}
                <span className="text-status-success font-medium" data-id="element-1864">
                  Complete
                </span>
              </li>
              <li className="flex justify-between" data-id="element-1865">
                <span data-id="element-1866">Tax & Bank</span>{' '}
                <span className="text-status-success font-medium" data-id="element-1867">
                  Verified
                </span>
              </li>
              <li className="flex justify-between" data-id="element-1868">
                <span data-id="element-1869">Compliance Docs</span>{' '}
                <span className="text-status-warning font-medium" data-id="element-1870">
                  1 Expiring
                </span>
              </li>
            </ul>
          </Card>

          {/* Quick Actions */}
          <Card data-id="element-1871">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1872">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-2" data-id="element-1873">
              <Button variant="secondary" size="sm" className="w-full justify-start" leftIcon={<TruckIcon className="w-4 h-4" data-id="element-1875" />} data-id="element-1874">
                Create ASN
              </Button>
              <Button variant="secondary" size="sm" className="w-full justify-start" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-1877" />} data-id="element-1876">
                Upload Doc
              </Button>
              <Button variant="secondary" size="sm" className="w-full justify-start" leftIcon={<MessageSquareIcon className="w-4 h-4" data-id="element-1879" />} data-id="element-1878">
                Support
              </Button>
              <Button variant="secondary" size="sm" className="w-full justify-start" leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-1881" />} data-id="element-1880">
                Orders
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>;
};